import React from 'react';
import Modal from '../ui/Modal';
import { PREDEFINED_OPP_SECTIONS } from '../../constants/oportunidadesConstants';

const OppSectionEditor = ({
  activeSection,
  answers,
  savingDraft,
  aiLoading,
  guide,
  usageLimits,
  onChangeAnswer,
  onApplyOption,
  onSaveDraft,
  onSuggest,
  onShowGuide,
  onCloseGuide,
}) => {
  if (!activeSection) return null;

  const isPredefined = PREDEFINED_OPP_SECTIONS.has(activeSection.section_title);

  const getRemainingForSection = () => {
    if (isPredefined) return Infinity;
    if (!usageLimits || !activeSection.section_title) return 2;
    const used = usageLimits.suggestions?.by_section?.[activeSection.section_title] || 0;
    const max = usageLimits.suggestions?.max_per_section || 2;
    return Math.max(0, max - used);
  };

  const remaining = getRemainingForSection();
  const isLimitReached = !isPredefined && remaining === 0;

  const getSuggestButtonLabel = () => {
    if (aiLoading) return isPredefined ? 'Cargando...' : 'Generando...';
    if (isPredefined) return 'Recomendaciones estratégicas';
    return `Sugerencias IA (${remaining}/2)`;
  };

  return (
    <section style={{ flex: 1, minWidth: 0 }}>
      <h2 className="section-title">{activeSection.section_title}</h2>

      <div className="actions-bar" style={{ justifyContent: 'flex-end', marginTop: 8, marginBottom: 12 }}>
        <button
          className="icon-btn"
          onClick={onShowGuide}
          title="Guía de esta sección"
          aria-label="Guía de esta sección"
        >
          i
        </button>
      </div>

      <div className="pitch-questions-list">
        {(activeSection.questions || []).map((q, idx) => (
          <div key={q.id} className="pitch-question-card">
            <div className="pitch-question-header">
              <span className="pitch-question-num">
                {String(idx + 1).padStart(2, '0')}
              </span>
              <p className="pitch-question-label">{q.question}</p>
            </div>

            {Array.isArray(q.options) && q.options.length > 0 && (
              <div className="pitch-quick-select-row">
                <span className="pitch-quick-select-label">Opciones rápidas</span>
                <select
                  className="pitch-quick-select"
                  value={q.options.includes(answers[q.id]) ? answers[q.id] : ''}
                  onChange={(e) => onApplyOption(q.id, e.target.value)}
                  aria-label={`Opciones para ${q.question}`}
                >
                  <option value="">Elige para autocompletar...</option>
                  {q.options.map((option) => (
                    <option key={`${q.id}-${option}`} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <textarea
              rows={2}
              className="pitch-answer-textarea"
              value={answers[q.id] || ''}
              onChange={(e) => onChangeAnswer(q.id, e.target.value)}
              placeholder={q.hint || 'Escribe tu respuesta...'}
            />
          </div>
        ))}
      </div>

      <div className="actions-bar">
        <button className="primary-btn" onClick={onSaveDraft} disabled={savingDraft}>
          {savingDraft ? 'Guardando...' : 'Guardar borrador'}
        </button>
        <button
          className={isPredefined ? 'secondary-btn predefined-btn' : 'secondary-btn'}
          onClick={onSuggest}
          disabled={aiLoading || isLimitReached}
          title={
            isLimitReached
              ? 'Has alcanzado el límite de sugerencias para esta sección hoy'
              : isPredefined
                ? 'Recomendaciones basadas en frameworks estratégicos'
                : ''
          }
        >
          {getSuggestButtonLabel()}
        </button>
      </div>

      {isLimitReached && (
        <div className="limit-warning">
          <span className="limit-icon">⏰</span>
          Has alcanzado el límite de 2 sugerencias para esta sección hoy. Vuelve mañana.
        </div>
      )}

      {!isPredefined && !isLimitReached && remaining < 2 && (
        <div className="usage-info">
          <span className="usage-info-icon">ℹ️</span>
          Te quedan {remaining} {remaining === 1 ? 'sugerencia' : 'sugerencias'} para esta sección hoy.
        </div>
      )}

      <Modal
        open={!!guide}
        title={guide ? `Guía: ${guide.section}` : ''}
        onClose={onCloseGuide}
      >
        {guide?.quick_tips?.length > 0 && (
          <>
            <h4>Tips rápidos</h4>
            <ul>
              {guide.quick_tips.map((tip, i) => (
                <li key={i}>{tip}</li>
              ))}
            </ul>
          </>
        )}
        {guide?.improvement_ideas?.length > 0 && (
          <>
            <h4>Ideas de mejora</h4>
            <ul>
              {guide.improvement_ideas.map((tip, i) => (
                <li key={i}>{tip}</li>
              ))}
            </ul>
          </>
        )}
      </Modal>
    </section>
  );
};

export default OppSectionEditor;
